import React from "react";
import { ExternalLink, MapPin } from "lucide-react";
import useFetch from "@/lib/useFetch";

// Fallback monument markers from the seed — only used when no real RustMaps data yet.
function pseudoMarkers(seed) {
    let s = Math.abs(parseInt(String(seed ?? "").replace(/\D/g, ""), 10) || 7);
    const pts = [];
    for (let i = 0; i < 7; i++) {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        const x = 10 + (s % 80);
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        const y = 10 + (s % 80);
        pts.push([x, y]);
    }
    return pts;
}

const GRID = {
    backgroundImage:
        "linear-gradient(rgba(255,107,0,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,0,0.10) 1px, transparent 1px)",
    backgroundSize: "16px 16px",
};

// Biome-ish colouring derived from the real monument type.
function monColor(type) {
    const t = (type || "").toLowerCase();
    if (/(harbor|fishing|lighthouse|oil rig|ferry|ocean|underwater)/.test(t)) return "#38bdf8"; // water
    if (/(launch|airfield|tunnel|arctic|missile|satellite|dome|power|water treatment|train|military)/.test(t)) return "#FF6B00"; // radtown
    if (/(outpost|bandit|town|safe|stable|ranch|fishing village)/.test(t)) return "#00FF66"; // safezone
    if (/(cave|canyon|lake|oasis|mountain|swamp|ice|sphere|junk)/.test(t)) return "#9ca3af"; // natural
    return "#D9480F";
}

const LEGEND = [
    ["#FF6B00", "Radtown"],
    ["#00FF66", "Safezone"],
    ["#38bdf8", "Water"],
    ["#9ca3af", "Natural"],
];

export default function MapPreview({ size, seed, testId }) {
    const path = size != null && seed != null
        ? `/maps/preview?size=${encodeURIComponent(size)}&seed=${encodeURIComponent(seed)}`
        : null;
    const { data } = useFetch(path);
    const mapUrl = data?.map_url || `https://rustmaps.com/map/${size}_${seed}`;
    const thumb = data?.thumbnail_url;
    const units = data?.map_size_units || 0;
    const monuments = data?.monuments || [];
    const total = data?.total_monuments || monuments.length;

    // Map world coords (-u/2..u/2) to tile %.
    const place = (m) => units
        ? { left: `${((m.x + units / 2) / units) * 100}%`, top: `${((units / 2 - m.y) / units) * 100}%` }
        : null;

    return (
        <div data-testid={testId} className="relative">
            <a href={mapUrl} target="_blank" rel="noreferrer"
                className="group relative block aspect-video w-full overflow-hidden rounded-md border border-border"
                style={{ background: "radial-gradient(circle at 32% 30%, #1b2512, #0c0e09 72%)" }}>
                {thumb ? (
                    <img src={thumb} alt={`Rust map ${size} ${seed}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                    <>
                        <div className="absolute inset-0 opacity-70" style={GRID} />
                        {monuments.length === 0 && pseudoMarkers(seed).map(([x, y], i) => (
                            <span key={`p-${x}-${y}`} className="absolute h-1.5 w-1.5 rounded-full bg-hazard/80"
                                style={{ left: `${x}%`, top: `${y}%`, boxShadow: "0 0 6px rgba(255,107,0,0.6)" }} />
                        ))}
                    </>
                )}

                {/* Real monument overlay */}
                {monuments.map((m, i) => {
                    const p = place(m);
                    if (!p) return null;
                    return (
                        <span key={`${m.type}-${m.x}-${m.y}-${i}`} title={m.type}
                            className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-black/50"
                            style={{ ...p, width: 6, height: 6, background: monColor(m.type) }} />
                    );
                })}

                {total > 0 && (
                    <span className="absolute left-2 top-2 flex items-center gap-1 rounded-sm border border-border bg-background/80 px-1.5 py-0.5 label-mono text-foreground">
                        <MapPin size={10} className="text-hazard" /> {total} monuments
                    </span>
                )}

                <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 bg-background/40">
                    <span className="flex items-center gap-1.5 rounded-md border border-hazard/50 bg-background/80 px-3 py-1.5 font-heading uppercase text-sm text-hazard">
                        <ExternalLink size={13} /> Open Interactive Map
                    </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-gradient-to-t from-background/95 to-transparent px-2.5 py-1.5">
                    <span className="label-mono text-muted-foreground">{size || "?"} · seed {seed ?? "?"}</span>
                    {data && !thumb && data.generating && <span className="label-mono text-warning">generating…</span>}
                </div>
            </a>
            {monuments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {LEGEND.map(([c, l]) => (
                        <span key={l} className="flex items-center gap-1 label-mono text-muted-foreground">
                            <span className="h-2 w-2 rounded-full" style={{ background: c }} /> {l}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
